import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme, type ThemePreference } from "../theme/ThemeProvider";
import { cn } from "./cn";

const ORDER: ThemePreference[] = ["light", "dark", "system"];

const ICONS = {
  light: Sun,
  dark: Moon,
  system: Monitor,
} as const;

/**
 * Three-state control rather than a binary switch, so "follow the OS" stays
 * reachable — a user who never picks anything keeps tracking their system.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme();
  const { t } = useTranslation();

  return (
    <div
      role="radiogroup"
      aria-label={t("theme.label", "Theme")}
      className={cn("inline-flex rounded-md border border-line bg-surface p-0.5", className)}
    >
      {ORDER.map((option) => {
        const Icon = ICONS[option];
        const active = preference === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            title={t(`theme.${option}`, option)}
            onClick={() => setPreference(option)}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded transition-colors max-md:size-9",
              active ? "bg-accent-soft text-accent" : "text-ink-faint hover:text-ink",
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
            <span className="sr-only">{t(`theme.${option}`, option)}</span>
          </button>
        );
      })}
    </div>
  );
}
